/////////////////////////////////////////////////////////////////
// src/pages/OrderHistory.jsx — v32
// CHANGED FROM v31 (22 Jul 2026) — second rework round from live
// feedback:
// A. ALL action buttons/forms (Track, Parcel Received, Invoice,
//    Initiate Return, the return-reason form, Contact Us, and the
//    Choose Refund/Wallet form + Confirm Choice) moved OUT of each
//    item's own card box entirely — the card now shows only photo,
//    name, status, and badges. Every action sits below the card, in
//    the shared order canvas, as its own sibling block. Each item
//    still gets its own action row (multi-item orders confirmed
//    unchanged — no shared/pooled row across items).
// B. Site-wide action color key LOCKED this session (first pass —
//    only Order History's buttons touched today, other pages to
//    follow when next worked on):
//      - Track = green (#1B7A3D)
//      - Download Invoice = off-white bg + gold text/border (#F4F2EE
//        bg, #D4A020 text+border — gold text needed a thin gold
//        border to stay visible against the white page background)
//      - All customer lifecycle actions = red (#D64545): Parcel
//        Received, Initiate Return, Submit Return Request, Confirm
//        Choice — previously Parcel Received was navy and Confirm
//        Choice was navy, both now red for consistency.
//    Light blue / dark blue deliberately left unassigned — no
//    Order History button needed them; candidates (Add to Cart,
//    Checkout, Contact Us, Sign in) deferred to whenever those pages
//    are actually touched, not decided blind today.
// C. Buy Again row — added a visible scroll arrow (right chevron,
//    floating circle) since the horizontal scroll had zero affordance
//    before; tapping it scrolls the row via a ref, no dependency on
//    the hidden native scrollbar.
// CHANGED FROM v30 (22 Jul 2026) — rework batch from live-testing
// feedback:
// A. "Submit Return Request" was clickable (and visually looked
//    active) before both required checkboxes were ticked — only
//    validated on click, with an alert. Now genuinely disabled
//    (grayed out, not clickable) until both boxes are checked, then
//    turns active red.
// B. "Showing X of Y" + page-size selector moved AGAIN, per Amit's
//    correction after seeing the first version — now sits in the
//    same row as the "Purchase history" label itself (right-aligned),
//    not as its own row above the order list.
// C. Download Invoice — still order-level (one action per order, not
//    per item) — is now merged into the SAME button row as Track/
//    Parcel Received, placed on the LAST item's card in that order
//    (previously its own separate full-width block below the item
//    list). Color changed from an invented green to gold (#D4A020),
//    matching the real site palette (navy / blue / off-white / gold)
//    — green was never one of the actual brand colors.
// CHANGED FROM v29 (22 Jul 2026) — P102 batch, original 6 items:
// 1. "Show X of Y" + page-size selector moved from the BOTTOM of the
//    page to directly beside "Purchase history" (same row, right-
//    aligned) — was previously near the search bar at the top, moved
//    once more per Amit's confirmation after the first mockup.
// 2. Search placeholder text changed to "Search by Product ID, Order
//    No." — was generic "Search order ID or product".
// 3. Date filter changed from a From/To date RANGE to a single Month +
//    Year picker (<input type="month">) — filteredOrders now matches
//    orders falling within that one calendar month instead of a range.
// 4. Per-item Track + Parcel Received buttons, and the per-order
//    Download Invoice button, were stacking as 3 separate full-width
//    blocks ("3 layers"). Track is now a compact colored button (blue)
//    instead of a full info card (courier name/AWB now shown as a
//    small text line above the button row, not inside a separate
//    card), sitting in ONE flex row alongside Parcel Received (navy).
//    Download Invoice (still order-level — one action for the whole
//    order, not per item) is now merged into that SAME row on the
//    LAST item card, instead of its own separate full-width block
//    below the item list — per Amit's explicit request to reduce its
//    size and put it in the same row. Color changed from green to
//    gold (var(--gold), matches the real site palette: navy/blue/
//    off-white/gold — green was never one of the actual brand colors).
// 5. Parcel Received now shows a celebratory popup on success — "Yey!!
//    Your cashback is on its way" with a lightweight CSS/JS confetti
//    burst (no external library available in this stack). Only fires
//    on a successful confirm, which by definition means no return has
//    been raised yet on that item (return requires Parcel Received to
//    happen first) — so no separate "if no returns" branch is needed.
// 6. Wallet-credit acknowledgment checkbox text at return-resolution
//    tightened for clarity — merged the confusing points 6+7 (min ₹25
//    balance / "amount over ₹25 can be used only") into one clear
//    line, reworded point 1. Still flagged for a fuller dedicated
//    review later per Amit's original note — this is a wording
//    clarity pass only, not a policy change.
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useStore } from '../context/StoreContext';
import { generateReceiptImage, downloadBlob } from '../utils/receipt';
import { getItemStageDisplay } from '../utils/orderStages';
const RETURN_VIDEO_FORM_URL = 'https://forms.gle/5T5U1teYmErF4zJ96';
const WHATSAPP_NUMBER = '918682054452';
const ELIGIBLE_CATEGORIES = ['Wrong Size Received', 'Wrong Color Received', 'Damaged Item Received'];
const INELIGIBLE_INFO = {
  'Size Issue': 'Size issue returns are not eligible',
  'Color Issue': 'Color issue returns are not eligible',
};
const INELIGIBLE_DETAIL = {
  'Size Issue': "Returns aren't accepted if you received the size you ordered — only if a different size was delivered by mistake.",
  'Color Issue': "Product colors may vary slightly from images due to lighting and screen settings. Returns aren't accepted for this — only if a different color was delivered by mistake.",
};
const ORDERS_PER_PAGE = 5;
function shareColorSizeUrl(orderId, item) {
  const msg = encodeURIComponent(
    `Hi, here's the color/style and size for ${item.productId} in my order #${orderId}. [attach your photo here]\nSize: `
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
}
function itemAttachmentUsable(it) {
  return !!(it.attachmentImageUrl && it.attachmentImageUrl !== 'Deleted after cashback credited');
}
function estimateCashback(it, earnRate) {
  const parsed = parseItemDescriptor(it.itemDescriptor);
  return Math.round(parsed.qty * parsed.price * (earnRate || 0.01) * 100) / 100;
}
function CashbackPendingBadge({ it, earnRate }) {
  if (it.cashbackStatus === 'Credited') {
    return (
      <div className="oh-cashback-badge credited">
        <i className="ti ti-check"></i> Cashback credited to wallet
      </div>
    );
  }
  if (it.cashbackStatus === 'Pending') {
    const amt = estimateCashback(it, earnRate);
    return (
      <div className="oh-cashback-badge pending">
        <i className="ti ti-clock"></i> ₹{amt} cashback coming to your wallet
      </div>
    );
  }
  if (it.cashbackStatus === 'Blocked') {
    return (
      <div className="oh-cashback-badge blocked">
        <i className="ti ti-info-circle"></i> Cashback on hold — return in progress
      </div>
    );
  }
  return null;
}
function parseItemDescriptor(desc) {
  const m = String(desc || '').match(/x(\d+)\s*@₹([\d.]+)/);
  return {
    qty: m ? parseInt(m[1], 10) : 1,
    price: m ? parseFloat(m[2]) : 0,
  };
}
function buildReceiptCartFromOrder(order) {
  return (order.items || []).map(it => {
    const parsed = parseItemDescriptor(it.itemDescriptor);
    return {
      id: it.productId,
      size: it.attachmentSize && it.attachmentSize !== 'Deleted after cashback credited' ? it.attachmentSize : '',
      qty: parsed.qty,
      price: parsed.price,
      colorCode: it.attachmentColorCode || '',
      key: it.productId,
    };
  });
}
function SkeletonOrderCard() {
  return (
    <div className="oh-skeleton-card">
      <div className="oh-skel-line" style={{ width: '40%', height: 14, marginBottom: 8 }}></div>
      <div className="oh-skel-line" style={{ width: '25%', height: 11, marginBottom: 14 }}></div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div className="oh-skel-box" style={{ width: 56, height: 56 }}></div>
        <div style={{ flex: 1 }}>
          <div className="oh-skel-line" style={{ width: '70%', height: 12, marginBottom: 6 }}></div>
          <div className="oh-skel-line" style={{ width: '40%', height: 10 }}></div>
        </div>
      </div>
    </div>
  );
}
export default function OrderHistory() {
  const { user, authLoading } = useStore();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyItemKey, setBusyItemKey] = useState(null);
  const [returnFormItemKey, setReturnFormItemKey] = useState(null);
  const [reasonCategory, setReasonCategory] = useState({});
  const [videoConfirmed, setVideoConfirmed] = useState({});
  const [proofAckConfirmed, setProofAckConfirmed] = useState({});
  const [choiceDrafts, setChoiceDrafts] = useState({});
  const [ackDrafts, setAckDrafts] = useState({});
  const [ineligibleModal, setIneligibleModal] = useState(null);
  const [search, setSearch] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [monthYearFilter, setMonthYearFilter] = useState(''); // NEW (v30): 'YYYY-MM', replaces dateFrom/dateTo range
  const [visibleCount, setVisibleCount] = useState(ORDERS_PER_PAGE);
  const [downloadingId, setDownloadingId] = useState(null);
  const [celebrateModal, setCelebrateModal] = useState(false); // NEW (v30): Parcel Received success popup
  const buyAgainScrollRef = useRef(null); // NEW (v32): for the Buy Again scroll arrow
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);
  const [parcelDateModal, setParcelDateModal] = useState(null); // { orderId, productId, lineKey }
  const [parcelDate, setParcelDate] = useState('');
  const [parcelHour, setParcelHour] = useState('');
  const [parcelMinute, setParcelMinute] = useState('');
  const [parcelAmPm, setParcelAmPm] = useState('PM');
  const fetchOrders = useCallback(async () => {
    if (!user || !user.customerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/get-customer-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: user.customerId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError('Could not load your orders. Please try again.');
      } else {
        setOrders(data.orders || []);
      }
    } catch (err) {
      setError('Network error loading orders.');
    }
    setLoading(false);
  }, [user]);
  useEffect(() => {
    if (authLoading) return;
    fetchOrders();
  }, [authLoading, fetchOrders]);
  function formatDateTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }
  function itemKeyFor(orderId, productId) {
    return orderId + '|' + productId;
  }
  function formatReturnWindowRemaining(parcelReceivedTime) {
    if (!parcelReceivedTime) return null;
    const hoursSince = (nowTick - new Date(parcelReceivedTime).getTime()) / (1000 * 60 * 60);
    const totalMinutesLeft = Math.round((24 - hoursSince) * 60);
    if (totalMinutesLeft <= 0) return null;
    const h = Math.floor(totalMinutesLeft / 60);
    const m = totalMinutesLeft % 60;
    if (h <= 0) return `${m}m left to raise a return`;
    return `${h}h ${m}m left to raise a return`;
  }
  function updateItemInState(orderId, productId, patch) {
    setOrders(prev =>
      prev.map(o => {
        if (o.orderId !== orderId) return o;
        return {
          ...o,
          items: (o.items || []).map(it => (it.productId === productId ? { ...it, ...patch } : it)),
        };
      })
    );
  }
  async function handleMarkParcelReceived(orderId, productId, lineKey, reportedDate, reportedTime) {
    const key = itemKeyFor(orderId, productId);
    setBusyItemKey(key);
    try {
      const res = await fetch('/.netlify/functions/confirm-delivery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, productId, lineKey, reportedReceivedAt: reportedDate && reportedTime ? `${reportedDate} ${reportedTime}` : '' }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not mark this item as received. Please try again.');
      } else {
        updateItemInState(orderId, productId, { parcelReceivedTime: new Date().toISOString(), cashbackStatus: 'Pending' });
        setCelebrateModal(true); // NEW (v30): success only reaches here, and a return can't
        // exist yet on this item (Initiate Return requires Parcel Received to have already
        // happened) — so this always means "no return raised", no extra check needed.
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
    setBusyItemKey(null);
  }
  function handleCategorySelect(orderId, productId, category) {
    const key = itemKeyFor(orderId, productId);
    if (category === 'Size Issue' || category === 'Color Issue') {
      setIneligibleModal(category);
      return;
    }
    if (category === 'Anything Else') {
      const msg = encodeURIComponent(
        `Hi, I have an issue with ${productId} in my order #${orderId} that isn't covered by the standard return categories. Can you help?`
      );
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank', 'noopener,noreferrer');
      return;
    }
    setReasonCategory(prev => ({ ...prev, [key]: category }));
  }
  async function handleInitiateReturn(orderId, productId, lineKey) {
    const key = itemKeyFor(orderId, productId);
    const category = reasonCategory[key];
    if (!category) {
      alert('Please select a reason.');
      return;
    }
    if (!videoConfirmed[key]) {
      alert('Please tick the box confirming your video upload before submitting.');
      return;
    }
    if (!proofAckConfirmed[key]) {
      alert('Please tick the second box confirming you understand the proof requirement.');
      return;
    }
    setBusyItemKey(key);
    try {
      const res = await fetch('/.netlify/functions/initiate-return', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, productId, reasonCategory: category, reasonDetail: '', lineKey }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not raise return request. Please try again.');
      } else {
        updateItemInState(orderId, productId, {
          returnStage: 'Requested',
          returnInitiatedTime: new Date().toISOString(),
          cashbackStatus: 'Blocked',
          returnReason: category,
        });
        setReturnFormItemKey(null);
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
    setBusyItemKey(null);
  }
  async function handleSubmitChoice(orderId, productId, lineKey) {
    const key = itemKeyFor(orderId, productId);
    const choice = choiceDrafts[key];
    if (!choice) {
      alert('Please select an option.');
      return;
    }
    if (choice === 'Wallet' && !ackDrafts[key]) {
      alert('Please tick the acknowledgment box to continue.');
      return;
    }
    setBusyItemKey(key);
    try {
      const res = await fetch('/.netlify/functions/choose-refund-wallet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, productId, choice, lineKey }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Could not process your choice. Please try again.');
      } else {
        updateItemInState(orderId, productId, { refundOrWalletChoice: choice });
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
    setBusyItemKey(null);
  }
  async function handleDownloadInvoice(order) {
    setDownloadingId(order.orderId);
    try {
      const cartForReceipt = buildReceiptCartFromOrder(order);
      const dateStr = formatDateTime(order.timestamp);
      const blob = await generateReceiptImage({ orderId: order.orderId, dateStr, total: order.orderTotal, cart: cartForReceipt });
      downloadBlob(blob, `${order.orderId}-invoice.png`);
    } catch (err) {
      alert('Could not generate invoice. Please try again.');
    }
    setDownloadingId(null);
  }
  const buyAgainItems = useMemo(() => {
    const seen = new Map();
    orders.forEach(order => {
      (order.items || []).forEach(it => {
        if (!itemAttachmentUsable(it)) return;
        if (!seen.has(it.productId)) {
          seen.set(it.productId, {
            productId: it.productId,
            itemDescriptor: it.itemDescriptor,
            imageUrl: it.attachmentImageUrl,
          });
        }
      });
    });
    return Array.from(seen.values());
  }, [orders]);
  const filteredOrders = useMemo(() => {
    let list = orders;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(o =>
        o.orderId.toLowerCase().includes(s) ||
        (o.items || []).some(it => it.productId.toLowerCase().includes(s))
      );
    }
    if (monthYearFilter) {
      // NEW (v30): monthYearFilter is 'YYYY-MM' from <input type="month">
      list = list.filter(o => {
        const d = new Date(o.timestamp);
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return ym === monthYearFilter;
      });
    }
    return list;
  }, [orders, search, monthYearFilter]);
  const visibleOrders = filteredOrders.slice(0, visibleCount);
  if (authLoading || loading) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
          <SkeletonOrderCard />
          <SkeletonOrderCard />
          <SkeletonOrderCard />
        </div>
        <style>{`
          .oh-skeleton-card { background: #fff; border-radius: 10px; border: 0.5px solid #e5e5e0; padding: 1rem; margin-bottom: 1rem; }
          .oh-skel-line, .oh-skel-box { background: #EDEBE5; border-radius: 4px; animation: ohPulse 1.3s ease-in-out infinite; }
          .oh-skel-box { border-radius: 6px; }
          @keyframes ohPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        `}</style>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>Please log in to view your order history.</p>
          <Link to="/login">Log in</Link>
        </div>
      </div>
    );
  }
  if (!user.customerId) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>Your account is still being set up. Please check back shortly.</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>{error}</p>
          <button onClick={fetchOrders}>Retry</button>
        </div>
      </div>
    );
  }
  if (orders.length === 0) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>You haven't placed any orders yet.</p>
          <Link to="/shop">Start shopping</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="app-shell">
      <Navbar />
      <div className="oh-page" style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
        {ineligibleModal && (
          <div
            onClick={() => setIneligibleModal(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', maxWidth: 340, textAlign: 'center' }}
            >
              <p style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 8, color: '#D64545' }}>
                {INELIGIBLE_INFO[ineligibleModal]}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: 14 }}>
                {INELIGIBLE_DETAIL[ineligibleModal]}
              </p>
              <Link
                to="/returns"
                style={{ display: 'inline-block', fontSize: '0.85rem', color: '#2F6FED', marginBottom: 14 }}
                onClick={() => setIneligibleModal(null)}
              >
                View Return Policy →
              </Link>
              <button
                onClick={() => setIneligibleModal(null)}
                style={{ display: 'block', width: '100%', background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 6, padding: '0.5rem' }}
              >
                Close
              </button>
            </div>
          </div>
        )}
        {parcelDateModal && (
          <div onClick={() => setParcelDateModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: '100%', maxWidth: 340 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1B2A4A', marginBottom: 10 }}>When did the parcel arrive?</p>
              <input type="date" value={parcelDate} onChange={e => setParcelDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10 }} />
              <p style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Approximate time</p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <select value={parcelHour} onChange={e => setParcelHour(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                  <option value="">Hour</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <select value={parcelMinute} onChange={e => setParcelMinute(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                  <option value="">Min</option>
                  {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={parcelAmPm} onChange={e => setParcelAmPm(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setParcelDateModal(null)} style={{ flex: 1, padding: 10, background: '#eee', border: 'none', borderRadius: 6, fontSize: 12 }}>Cancel</button>
                <button
                  onClick={() => {
                    let h24 = parseInt(parcelHour, 10);
                    if (parcelAmPm === 'PM' && h24 !== 12) h24 += 12;
                    if (parcelAmPm === 'AM' && h24 === 12) h24 = 0;
                    const time24 = `${String(h24).padStart(2, '0')}:${parcelMinute}`;
                    handleMarkParcelReceived(parcelDateModal.orderId, parcelDateModal.productId, parcelDateModal.lineKey, parcelDate, time24);
                    setParcelDateModal(null);
                  }}
                  disabled={!parcelDate || !parcelHour || !parcelMinute}
                  style={{ flex: 1, padding: 10, background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12 }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
        {celebrateModal && (
          <div className="oh-celebrate-overlay" onClick={() => setCelebrateModal(false)}>
            {Array.from({ length: 40 }).map((_, i) => {
              const colors = ['#FF3B5C', '#1B7A3D', '#2F6FED', '#FFC93C', '#FF7A45', '#00B8A9'];
              const left = Math.random() * 100;
              const delay = Math.random() * 0.4;
              const duration = 1.8 + Math.random() * 1.2;
              const color = colors[i % colors.length];
              return (
                <div
                  key={i}
                  className="oh-confetti-piece"
                  style={{
                    left: `${left}%`,
                    background: color,
                    animationDelay: `${delay}s`,
                    animationDuration: `${duration}s`,
                  }}
                />
              );
            })}
            <div className="oh-celebrate-card" onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
              <p style={{ fontSize: 17, fontWeight: 700, color: '#1B2A4A', marginBottom: 6 }}>Yey!! Your cashback is on its way</p>
              <p style={{ fontSize: 12, color: '#666', marginBottom: 18 }}>
                It'll land in your nb wallet 24 hours after this confirmation, as long as no return is raised.
              </p>
              <button
                onClick={() => setCelebrateModal(false)}
                style={{ background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.5rem', fontSize: 13, fontWeight: 600 }}
              >
                Nice!
              </button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', marginTop: '1rem' }}>
          <div className="oh-search-box">
            <i className="ti ti-search" style={{ fontSize: 16, color: '#999' }}></i>
            <input
              type="text"
              placeholder="Search by Product ID, Order No."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            className="oh-filter-btn"
            onClick={() => setShowDateFilter(v => !v)}
            aria-label="Filter by date"
            style={{ background: showDateFilter ? '#EEF4FF' : '#fff' }}
          >
            <i className="ti ti-calendar"></i>
          </button>
        </div>
        {showDateFilter && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#888' }}>Month</label>
            <input type="month" value={monthYearFilter} onChange={e => setMonthYearFilter(e.target.value)} style={{ padding: '6px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12 }} />
            {monthYearFilter && (
              <button onClick={() => setMonthYearFilter('')} style={{ fontSize: 11, color: '#2F6FED', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
            )}
          </div>
        )}
        {buyAgainItems.length > 0 && (
          <div className="oh-buyagain-section">
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Buy again</div>
            <div className="oh-buyagain-wrap">
              <div className="oh-buyagain-row" ref={buyAgainScrollRef}>
                {buyAgainItems.map(it => (
                  <div key={it.productId} className="oh-buyagain-item" onClick={() => navigate(`/product/${it.productId}`)}>
                    <div className="oh-buyagain-thumb">
                      {it.imageUrl ? <img src={it.imageUrl} alt={it.itemDescriptor} /> : <i className="ti ti-photo" style={{ fontSize: 22, color: '#ccc' }}></i>}
                    </div>
                    <div className="oh-buyagain-label">{it.productId}</div>
                  </div>
                ))}
                {buyAgainItems.length > 0 && (
                  <div
                    className="oh-buyagain-item oh-buyagain-seeall"
                    onClick={() => document.getElementById('oh-purchase-history')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="oh-buyagain-thumb oh-buyagain-seeall-thumb">
                      <i className="ti ti-arrow-right" style={{ fontSize: 20 }}></i>
                    </div>
                    <div className="oh-buyagain-label">See all</div>
                  </div>
                )}
              </div>
              {buyAgainItems.length > 2 && (
                <button
                  className="oh-buyagain-scroll-arrow"
                  onClick={() => buyAgainScrollRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
                  aria-label="Scroll for more"
                >
                  <i className="ti ti-chevron-right"></i>
                </button>
              )}
            </div>
          </div>
        )}
        <div id="oh-purchase-history" className="oh-purchase-header">
          <span style={{ fontSize: 14, fontWeight: 500 }}>Purchase history</span>
          {filteredOrders.length > 0 && (
            <div className="oh-showing-row">
              <span>Showing {visibleOrders.length} of {filteredOrders.length}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Show</span>
                <select
                  value={visibleCount >= filteredOrders.length ? 'all' : visibleCount}
                  onChange={e => setVisibleCount(e.target.value === 'all' ? filteredOrders.length : Number(e.target.value))}
                  style={{ padding: '5px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, background: '#fff', color: '#1B2A4A', fontFamily: 'var(--font)' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          )}
        </div>
        {filteredOrders.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '2rem 0' }}>No orders match your search.</p>
        ) : (
          visibleOrders.map(order => {
            const items = order.items || [];
            return (
              <div key={order.orderId} className="oh-order-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <p style={{ fontWeight: 600, fontSize: 13 }}>Order #{order.orderId}</p>
                  <p style={{ fontSize: '0.72rem', color: '#888', whiteSpace: 'nowrap', marginLeft: 8 }}>{formatDateTime(order.timestamp)}</p>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: 10 }}>₹{order.orderTotal} · {order.status}</p>
                {items.map((it, idx) => {
                  const key = itemKeyFor(order.orderId, it.productId);
                  const display = getItemStageDisplay(it);
                  const track = it.returnStage ? null : it.trackingUrl;
                  const thumbUrl = itemAttachmentUsable(it) ? it.attachmentImageUrl : '';
                  const busy = busyItemKey === key;
                  const isLastItem = idx === items.length - 1;
                  return (
                    <React.Fragment key={it.productId}>
                      <div className="oh-item-card">
                        <Link to={`/product/${it.productId}`} className="oh-item-thumb">
                          {thumbUrl ? <img src={thumbUrl} alt={it.itemDescriptor} /> : <i className="ti ti-photo" style={{ fontSize: 22, color: '#ccc' }}></i>}
                        </Link>
                        <div className="oh-item-info">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <Link to={`/product/${it.productId}`} className="oh-item-name">{it.itemDescriptor}</Link>
                              <div className="oh-item-orderid">Order {order.orderId}</div>
                              <div className="oh-item-status" style={{ color: display.color }}>{display.label}</div>
                              {display.secondaryLabel && (
                                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{display.secondaryLabel}</div>
                              )}
                              {!itemAttachmentUsable(it) && !it.dispatchedDate && (
                                <a href={shareColorSizeUrl(order.orderId, it)} target="_blank" rel="noopener noreferrer" className="oh-item-action-link">
                                  Share Color & Size →
                                </a>
                              )}
                              <CashbackPendingBadge it={it} earnRate={user?.wallet?.earnRate} />
                            </div>
                          </div>
                          {track && it.awb && (
                            <div className="oh-track-meta">{it.courierPartner || 'Courier'} · AWB {it.awb}</div>
                          )}
                        </div>
                      </div>
                      {/* NEW (v32): every action button/form for this item now sits OUTSIDE the
                          item's own card box, in the shared order canvas below it — per Amit's
                          explicit request. The card above stays purely informational. */}
                      {(track || display.showParcelReceivedButton || isLastItem) && (
                        <div className="oh-btn-row">
                          {track && (
                            <a href={track} target="_blank" rel="noopener noreferrer" className="oh-row-btn oh-row-btn-track">
                              Track
                            </a>
                          )}
                          {display.showParcelReceivedButton && (
                            <button
                              onClick={() => { setParcelDate(''); setParcelHour(''); setParcelMinute(''); setParcelAmPm('PM'); setParcelDateModal({ orderId: order.orderId, productId: it.productId, lineKey: it.lineKey }); }}
                              disabled={busy}
                              className="oh-row-btn oh-row-btn-received"
                            >
                              {busy ? 'Confirming...' : 'Parcel Received'}
                            </button>
                          )}
                          {isLastItem && (
                            <button
                              onClick={() => handleDownloadInvoice(order)}
                              disabled={downloadingId === order.orderId}
                              className="oh-row-btn oh-row-btn-invoice"
                            >
                              <i className="ti ti-download" style={{ marginRight: 4 }}></i>
                              {downloadingId === order.orderId ? 'Preparing…' : 'Invoice'}
                            </button>
                          )}
                        </div>
                      )}
                      {display.showInitiateReturnButton && returnFormItemKey !== key && (
                        <>
                        <button
                          onClick={() => setReturnFormItemKey(key)}
                          className="oh-action-btn"
                          style={{ background: '#D64545' }}
                        >
                          Initiate Return
                        </button>
                        {formatReturnWindowRemaining(it.parcelReceivedTime) && (
                          <div style={{ fontSize: 10, color: '#D64545', marginTop: 4, marginBottom: 8 }}>
                            <i className="ti ti-clock" style={{ fontSize: 11, verticalAlign: -1, marginRight: 3 }}></i>
                            {formatReturnWindowRemaining(it.parcelReceivedTime)}
                          </div>
                        )}
                        </>
                      )}
                      {display.showInitiateReturnButton && returnFormItemKey === key && (
                        <div className="oh-return-form">
                          <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>What's the issue?</p>
                          {ELIGIBLE_CATEGORIES.map(option => (
                            <label key={option} style={{ display: 'block', fontSize: '0.85rem', marginBottom: 6 }}>
                              <input
                                type="radio"
                                name={`reason-${key}`}
                                checked={reasonCategory[key] === option}
                                onChange={() => handleCategorySelect(order.orderId, it.productId, option)}
                                style={{ marginRight: 6 }}
                              />
                              {option}
                            </label>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleCategorySelect(order.orderId, it.productId, 'Size Issue')}
                            className="oh-ineligible-link"
                          >
                            Size Issue (received the size I ordered, doesn't fit) →
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCategorySelect(order.orderId, it.productId, 'Color Issue')}
                            className="oh-ineligible-link"
                          >
                            Color Issue (received the color I ordered, looks different) →
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCategorySelect(order.orderId, it.productId, 'Anything Else')}
                            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '4px 0 8px', fontSize: '0.85rem', color: '#2F6FED', cursor: 'pointer' }}
                          >
                            Anything else — message us on WhatsApp →
                          </button>
                          {ELIGIBLE_CATEGORIES.includes(reasonCategory[key]) && (
                            <>
                              <div style={{ marginTop: 6, marginBottom: 8 }}>
                                <a href={RETURN_VIDEO_FORM_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: '#2F6FED' }}>
                                  → Upload your video here (name it with your Order ID)
                                </a>
                                <label style={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.75rem', color: '#555', marginTop: 6 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!videoConfirmed[key]}
                                    onChange={e => setVideoConfirmed(prev => ({ ...prev, [key]: e.target.checked }))}
                                    style={{ marginRight: 6, marginTop: 2 }}
                                  />
                                  I confirm I have uploaded a clear, uncut, unboxing video showing the issue, as required for this return type.
                                </label>
                                <label style={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.75rem', color: '#555', marginTop: 6 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!proofAckConfirmed[key]}
                                    onChange={e => setProofAckConfirmed(prev => ({ ...prev, [key]: e.target.checked }))}
                                    style={{ marginRight: 6, marginTop: 2 }}
                                  />
                                  I understand that if proof is not attached, my return request will not be actioned or responded to.
                                </label>
                              </div>
                              <button
                                onClick={() => handleInitiateReturn(order.orderId, it.productId, it.lineKey)}
                                disabled={busy || !videoConfirmed[key] || !proofAckConfirmed[key]}
                                style={{ background: (videoConfirmed[key] && proofAckConfirmed[key]) ? '#D64545' : '#E8A0A0', color: '#fff', border: 'none', borderRadius: 6, padding: '0.5rem 1rem', fontSize: '0.85rem', width: '100%', cursor: (videoConfirmed[key] && proofAckConfirmed[key]) ? 'pointer' : 'not-allowed' }}
                              >
                                {busy ? 'Submitting...' : 'Submit Return Request'}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      {display.showContactUs && (
                        <div style={{ background: '#FBEAEA', borderRadius: 6, padding: '0.6rem', marginTop: 6, marginBottom: 8 }}>
                          {it.returnRejectedNote && (
                            <p style={{ fontSize: '0.78rem', color: '#555', marginBottom: 6 }}>{it.returnRejectedNote}</p>
                          )}
                          <a
                            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I'd like to follow up on my return request for ${it.productId} in order #${order.orderId} which wasn't approved.`)}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-block', background: '#2F6FED', color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: '0.75rem', textDecoration: 'none' }}
                          >
                            Contact Us
                          </a>
                        </div>
                      )}
                      {display.showChoiceButtons && (
                        <div className="oh-return-form">
                          <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>How would you like this settled?</p>
                          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 6 }}>
                            <input
                              type="radio"
                              name={`choice-${key}`}
                              checked={choiceDrafts[key] === 'Refund'}
                              onChange={() => setChoiceDrafts(prev => ({ ...prev, [key]: 'Refund' }))}
                              style={{ marginRight: 6 }}
                            />
                            Refund to original payment method (2–3 days)
                          </label>
                          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 8 }}>
                            <input
                              type="radio"
                              name={`choice-${key}`}
                              checked={choiceDrafts[key] === 'Wallet'}
                              onChange={() => setChoiceDrafts(prev => ({ ...prev, [key]: 'Wallet' }))}
                              style={{ marginRight: 6 }}
                            />
                            Instant nb wallet credit
                          </label>
                          {choiceDrafts[key] === 'Wallet' && (
                            <label style={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.75rem', color: '#555', marginBottom: 8 }}>
                              <input
                                type="checkbox"
                                checked={!!ackDrafts[key]}
                                onChange={e => setAckDrafts(prev => ({ ...prev, [key]: e.target.checked }))}
                                style={{ marginRight: 6, marginTop: 2 }}
                              />
                              <span>
                                I understand that:
                                <br />1. This amount will be credited to my nb wallet as cashback — not refunded to my bank
                                <br />2. nb wallet credit cannot be transferred to my bank account
                                <br />3. This credit is valid for 180 days from today
                                <br />4. This does not count towards moving up a loyalty tier
                                <br />5. On any future order, I can use at most 50% of that order's value from my wallet
                                <br />6. My wallet balance must stay above ₹25 at all times — only the amount above that can be used
                              </span>
                            </label>
                          )}
                          <button
                            onClick={() => handleSubmitChoice(order.orderId, it.productId, it.lineKey)}
                            disabled={busy}
                            className="oh-row-btn oh-row-btn-choice"
                            style={{ width: '100%' }}
                          >
                            {busy ? 'Processing...' : 'Confirm Choice'}
                          </button>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
      <style>{`
        .oh-purchase-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; margin-top: 4px; flex-wrap: wrap; gap: 6px; }
        .oh-showing-row { display: flex; align-items: center; gap: 10px; font-size: 11px; color: #888; }
        .oh-celebrate-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1100; padding: 1rem; overflow: hidden; }
        .oh-celebrate-card { background: #fff; border-radius: 14px; padding: 2rem 1.5rem; text-align: center; max-width: 320px; position: relative; z-index: 2; box-shadow: 0 8px 28px rgba(0,0,0,0.25); }
        .oh-confetti-piece { position: absolute; top: -10px; width: 8px; height: 14px; opacity: 0.9; animation: ohConfettiFall linear forwards; z-index: 1; }
        @keyframes ohConfettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0.7; }
        }
        .oh-search-box { flex: 1; display: flex; align-items: center; gap: 6px; background: #fff; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; padding: 0 10px; height: 36px; }
        .oh-search-box input { border: none; outline: none; font-size: 13px; flex: 1; background: none; }
        .oh-filter-btn { width: 36px; height: 36px; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #1B2A4A; }
        .oh-buyagain-section { background: #EEF4FF; border-radius: 10px; padding: 12px; margin-bottom: 1.5rem; }
        .oh-buyagain-wrap { position: relative; }
        .oh-buyagain-row { display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none; }
        .oh-buyagain-row::-webkit-scrollbar { display: none; }
        .oh-buyagain-scroll-arrow { position: absolute; right: 2px; top: 50%; transform: translateY(-50%); width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,0.92); border: none; box-shadow: 0 1px 4px rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; cursor: pointer; color: #1B2A4A; font-size: 16px; }
        .oh-buyagain-item { flex-shrink: 0; width: 72px; cursor: pointer; }
        .oh-buyagain-thumb { width: 72px; height: 72px; border-radius: 6px; background: #fff; border: 0.5px solid rgba(27,42,74,0.12); display: flex; align-items: center; justify-content: center; overflow: hidden; margin-bottom: 4px; }
        .oh-buyagain-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .oh-buyagain-label { font-size: 10px; color: #555; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .oh-buyagain-seeall-thumb { background: var(--navy); color: #fff; }
        .oh-order-group { background: #fff; border-radius: 10px; border: 0.5px solid #e5e5e0; padding: 1rem; margin-bottom: 1rem; }
        .oh-item-card { display: flex; gap: 10px; border: 0.5px solid rgba(27,42,74,0.12); border-radius: 6px; padding: 10px; margin-bottom: 8px; background: #FAFAF8; }
        .oh-item-thumb { width: 56px; height: 56px; border-radius: 6px; background: #F4F2EE; border: 0.5px solid rgba(27,42,74,0.12); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; }
        .oh-item-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .oh-item-info { flex: 1; min-width: 0; }
        .oh-item-name { display: block; font-size: 12px; font-weight: 500; color: #1B2A4A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 2px; text-decoration: none; }
        .oh-item-orderid { font-size: 10px; color: #999; margin-bottom: 4px; }
        .oh-item-status { font-size: 11px; font-weight: 600; }
        .oh-item-action-link { display: inline-block; margin-top: 4px; font-size: 10px; color: #2F6FED; text-decoration: none; }
        /* NEW (v30): Track / Parcel Received / Download Invoice were
           stacking as 3 separate full-width "layers" — now compact
           buttons sitting in one flex row, each a distinct color, per
           Amit's explicit request. Courier + AWB shown as one small
           text line above the row instead of their own card. */
        .oh-track-meta { font-size: 10px; color: #8899BB; margin-top: 8px; }
        .oh-btn-row { display: flex; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
        .oh-row-btn { flex: 1; min-width: 0; text-align: center; padding: 8px 10px; border: none; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; text-decoration: none; box-sizing: border-box; white-space: nowrap; }
        .oh-row-btn-track { background: #1B7A3D; color: #fff; }
        .oh-row-btn-received { background: #D64545; color: #fff; }
        .oh-row-btn-invoice { background: #F4F2EE; color: #D4A020; border: 1px solid #D4A020; display: flex; align-items: center; justify-content: center; }
        .oh-row-btn-choice { background: #D64545; color: #fff; border: none; border-radius: 6px; padding: 0.5rem 1rem; font-size: 0.85rem; }
        .oh-cashback-badge { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; margin-top: 5px; padding: 3px 7px; border-radius: 5px; width: fit-content; }
        .oh-cashback-badge i { font-size: 12px; }
        .oh-cashback-badge.pending { background: #FEF8E8; color: #854F0B; }
        .oh-cashback-badge.credited { background: #E8F5E9; color: var(--green); }
        .oh-cashback-badge.blocked { background: #F4F2EE; color: var(--muted); }
        .oh-action-btn { display: block; color: #fff; border: none; border-radius: 6px; padding: 6px 12px; font-size: 11px; margin-top: 8px; cursor: pointer; }
        .oh-return-form { margin-top: 8px; background: #F4F2EE; border-radius: 8px; padding: 0.75rem; }
        .oh-ineligible-link { display: block; width: 100%; text-align: left; background: none; border: none; padding: 4px 0; font-size: 0.85rem; color: #8A5A00; cursor: pointer; }
      `}</style>
    </div>
  );
}